import { CreateButton, EditButton, SaveButton } from '@refinedev/antd';
import { ToolList, embeddedTableProps } from '../../components/ToolList';
import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';

import {
  IResourceComponentsProps,
  useCustomMutation,
  useGetIdentity,
  useGetLocale,
  useTranslate,
} from '@refinedev/core';

import { useCcEditableTable } from '../../helpers';
import { getYupSyncValidator } from '../../helpers/validation';

import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  DayReminders,
  dbCols,
  getValidators,
  Thought,
  UserRole,
} from '@cc/schema';
import { useActor } from '@xstate/react';
import { RuleObject } from 'antd/lib/form';
import { format, set } from 'date-fns';
import { useContext, useEffect, useMemo, useState } from 'react';
import ReactCardFlip from 'react-card-flip';
import { useTranslation } from 'react-i18next';
import { TherapistActivityReactContext } from '../../App';
import { useFormSubmitDisabledAnyTouched } from '../../hooks';
import {
  ClientName,
  ShowNamesButton,
  clientFilterText,
  useClientNamesVisibility,
} from '../../components/clientNames';
import { useClientRoster } from '../../hooks/client-options';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { backToListEvent } from '../../hooks/activityEvents';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { IUser } from '../../providers';
import { DateFnsTimePicker } from '../../components/Pickers';
import { CardRemindersCell } from '../../components/EsCardReminders';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

/**
 * E57 · EMBEDDED on the client page with the client FIXED (`fixedClientId` — registry decision `per-client-views`):
 * the same list, filtered to that client whatever the header bar's filter says, with no client column; Create opens the
 * create page with `?clientId=` (Q-e57g1-2 A rule 4); the page that hosts it records the RTM page view (rule 2), so
 * the list's own tracking is off. Standalone (no `fixedClientId`) it behaves exactly as before.
 */
export const CopingCardsList: React.FC<
  IResourceComponentsProps & { fixedClientId?: string }
> = ({ fixedClientId }) => {
  const translate = useTranslate();
  const { t, i18n } = useTranslation();

  const therapistActivityService = useContext(TherapistActivityReactContext);

  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );
  // E57 — the client page fixes the client; otherwise the header bar's filter
  const clientFilterId = fixedClientId || therapistActivityState?.context?.filterClientId;
  const navigateTo = useNavigate();

  const [isCardFlipped, setIsCardFlipped] = useState(false);

  const locale = useGetLocale();

  const currentLocale = locale();

  const {
    data: identity,
    remove,
    refetch: refetchIdentity,
  } = useGetIdentity<IUser>();

  const { portalThoughtUpdateValidator } = useMemo(
    () => getValidators(translate),
    [i18n.languages[0]],
  );

  const validationRules = getYupSyncValidator(
    translate,
    portalThoughtUpdateValidator,
  ) as RuleObject;

  const { mutate: mutateDayReminders } = useCustomMutation<DayReminders>();

  // the WHOLE practice roster (P011) — the client column, the client filter and the edit gate below
  // all have to be able to name the owner of every row this table can render
  // Q-mt-3b A: "id · initials", full name on hover; Show names for 60 s
  const names = useClientNamesVisibility();
  const { clients } = useClientRoster();

  const {
    table: { tableQueryResult, tableProps, setFilters },
    form: { formProps },
    isEditing,
    saveButtonProps,
    cancelButtonProps,
    editButtonProps,
  } = useCcEditableTable<Thought>(
    {
      // E57: the resource is named, never inferred from the route (embedded on the client page the route's is `clients`)
      resource: 'portalCards',
      queryOptions: {
        select: (data) => {
          // @ts-ignore
          return { data: data.data.data, total: data.total };
        },
      },
      sorters: {
        initial: [
          {
            field: 'updatedAt',
            order: 'desc',
          },
        ],
      },

      filters: {
        initial: clientFilterId
          ? [
              {
                field: 'userId',
                operator: 'eq',
                value: clientFilterId,
              },
            ]
          : [],
      },

      meta: {
        fields: [
          {
            data: [
              '_id',
              'userId',
              'automaticThought',
              { realisticThoughts: ['thought', 'thoughtBelief'] },
              'updatedAt',
              {
                dayReminders: [
                  '_id',
                  { morning: [{ time: ['hours', 'minutes'] }, 'enabled'] },
                  { day: [{ time: ['hours', 'minutes'] }, 'enabled'] },
                  { evening: [{ time: ['hours', 'minutes'] }, 'enabled'] },
                ],
              },
            ],
          },
          'count',
        ],
      },
    },
    {
      resource: 'portalCards',
      onMutationSuccess: () => {
        cancelButtonProps.onClick?.();
      },
      meta: {
        fields: [
          '_id',
          'userId',
          'automaticThought',
          { realisticThoughts: ['thought', 'thoughtBelief'] },
          {
            dayReminders: [
              { morning: [{ time: ['hours', 'minutes'] }, 'enabled'] },
              { day: [{ time: ['hours', 'minutes'] }, 'enabled'] },
              { evening: [{ time: ['hours', 'minutes'] }, 'enabled'] },
            ],
          },
          'updatedAt',
        ],
      },
    },
  );

  const updateDateReminders = async (dayReminders: DayReminders) => {
    await mutateDayReminders({
      url: '',
      method: 'post',
      values: {},
      meta: {
        operation: 'updateDayReminders',
        fields: [{ dayReminders: ['_id'] }],
        variables: {
          dayReminders: {
            name: 'dayReminders',
            type: 'DayRemindersUpdateInput!',
            value: dayReminders,
          },
        },
      },
    });
    // await tableQueryResult?.refetch();
    saveButtonProps.onClick?.();
  };

  useEffect(() => {
    setFilters(
      clientFilterId
        ? [
            {
              field: 'userId',
              operator: 'eq',
              value: clientFilterId,
            },
          ]
        : [],
      'replace',
    );
  }, [clientFilterId]);

  const {
    handleFormChange,
    isDisabled: isSubmitDisabled,
    fieldsCount,
  } = useFormSubmitDisabledAnyTouched(formProps?.form!);

  useActivityTracking(t('pages.clientsShow.therapistOtherResources.portalCards'), fixedClientId, !fixedClientId);

  const expandedRowRender = (record: Thought) => {
    return (
      <div
        style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
        }}
      >
        <div style={{ width: 400 }}>
          <ReactCardFlip
            isFlipped={isCardFlipped}
            flipDirection="horizontal"
            flipSpeedBackToFront={2}
            flipSpeedFrontToBack={2}
          >
            <Card
              style={{ width: 400 }}
              onClick={() => setIsCardFlipped(!isCardFlipped)}
              bordered
              hoverable
              bodyStyle={{
                minHeight: 200,
                boxShadow: '0 0 10px #ccc',
                background: '#45403e',
                color: '#f5f1f1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 10,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {(record.automaticThought || '').split('\n').map((t, idx) => {
                  return <div key={idx}>{t}</div>;
                })}
              </div>
            </Card>

            <Card
              style={{ width: 400 }}
              onClick={() => setIsCardFlipped(!isCardFlipped)}
              bordered
              hoverable
              bodyStyle={{
                minHeight: 200,
                boxShadow: '0 0 10px #ccc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 10,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {record.realisticThoughts?.map((t: any, i: number) => {
                  return (t.thought || '')
                    .split('\n')
                    .map((th: string, idx: number) => {
                      const prefix =
                        i === 0 ? '' : idx === 0 ? `${i + 1}. ` : '';
                      return <div key={idx}>{`${prefix}${th}`}</div>;
                    });
                })}
              </div>
            </Card>
          </ReactCardFlip>
        </div>
      </div>
    );
  };

  return (
    <ToolList
      fixedClientId={fixedClientId}
      tableProps={tableProps}
      breadcrumb={fixedClientId ? false : undefined}
      title={translate('pages.copingCards.title')}
      headerProps={{
        extra: (
          <Space>
            {fixedClientId ? null : (
              <ShowNamesButton
                visible={names.visible}
                onToggle={names.toggle}
                testId="cc-coping-cards-toggle-names"
              />
            )}
            <CreateButton
            data-testid="create-coping-card-button"
            onClick={
              fixedClientId
                ? () => navigateTo(`/tools/portalCards/create?clientId=${encodeURIComponent(fixedClientId)}`)
                : undefined
            }
          />
          </Space>
        ),
      }}
    >
      <Form {...formProps} onFieldsChange={handleFormChange}>
        <Table
          {...embeddedTableProps(fixedClientId)}
          {...tableProps}
          rowKey="_id"
          expandable={{
            expandedRowRender,
          }}
          onChange={(pagination, filters, sorter, extra) => {
            // Note: The data we get from Ant.design filters does not match the
            // react refine one. Currently it works fine if I simply silence it the way below.
            // TOOD: Find a way to convert the data to match the react refine one and fix it better
            // @ts-ignore
            tableProps?.onChange?.(pagination, null, sorter, extra);
          }}
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: true,
          }}
          data-testid="coping-cards-list"
        >
          {fixedClientId ? null : (
          <Table.Column
            key="_id"
            dataIndex="userId"
            sorter={{ multiple: 2 }}
            title={translate('pages.thoughtList.userId')}
            filterSearch
            // @ts-ignore
            onFilter={(value: string, record: Thought) => {
              return record.userId === value;
            }}
            filters={clients.map((c) => {
              return {
                text: clientFilterText(c, names.visible),
                value: c._id,
              };
            })}
            render={(value, record) => {
              const client = clients.find((c) => c._id === value);

              return (
                <ClientName
                  id={value}
                  firstName={client?.firstName}
                  lastName={client?.lastName}
                  visible={names.visible}
                />
              );
            }}
          />
          )}
          <Table.Column<Thought>
            key="automaticThought"
            dataIndex="automaticThought"
            title={
              <>
                {translate('pages.copingCards.frontSide')}
                <Tooltip title={translate('pages.copingCards.frontSideTip')}>
                  <QuestionCircleOutlined style={{ marginLeft: '10px' }} />
                </Tooltip>
              </>
            }
            render={(value: string, record) => {
              if (isEditing(record._id)) {
                return (
                  <Form.Item
                    name={['automaticThought']}
                    style={{ margin: 0, minWidth: 120 }}
                    rules={[validationRules]}
                  >
                    <Input.TextArea
                      data-testid="cc-card-edit-front"
                      autoSize={{ minRows: 4 }}
                    />
                  </Form.Item>
                );
              }
              return (
                <>
                  {(value || '').split('\n').map((t, idx) => {
                    return <div key={idx}>{t}</div>;
                  })}
                </>
              );
            }}
          />
          <Table.Column<Thought>
            key="realisticThoughts"
            dataIndex={['realisticThoughts']}
            title={
              <>
                {translate('pages.copingCards.backSide')}
                <Tooltip title={translate('pages.copingCards.backSideTip')}>
                  <QuestionCircleOutlined style={{ marginLeft: '10px' }} />
                </Tooltip>
              </>
            }
            render={(thoughts, record, indx) => {
              if (isEditing(record._id)) {
                return (
                  <Form.List name={['realisticThoughts']}>
                    {(fields, { add, remove }, errors) => {
                      return (
                        <>
                          {fields.map((field, index) => {
                            return (
                              <Form.Item
                                {...field}
                                key={field.key}
                                name={[field.key, 'thought']}
                                style={{ margin: 0, minWidth: 140 }}
                                rules={[
                                  {
                                    required: true,
                                    message: translate(
                                      'createThought.realisticThoughtRequired',
                                    ),
                                  },
                                ]}
                              >
                                <Input.TextArea
                                  data-testid="cc-card-edit-back"
                                  autoSize={{ minRows: 3 }}
                                />
                              </Form.Item>
                            );
                          })}
                        </>
                      );
                    }}
                  </Form.List>
                );
              }
              return (
                <div>
                  {
                    // @ts-ignore
                    thoughts.map((t, i) => {
                      return (
                        <div key={i}>
                          <>
                            {(t.thought || '')
                              .split('\n')
                              .map((th: string, idx: number) => {
                                const prefix =
                                  i === 0 ? '' : idx === 0 ? `${i + 1}. ` : '';
                                return <div key={idx}>{`${prefix}${th}`}</div>;
                              })}
                          </>
                        </div>
                      );
                    })
                  }
                </div>
              );
            }}
          />
          <Table.Column
            key="dayReminders"
            dataIndex="dayReminders"
            title={
              <>
                {translate('pages.copingCards.timeBasedReminders')}
                <Tooltip
                  title={translate('pages.copingCards.timeBasedRemindersTip')}
                >
                  <QuestionCircleOutlined style={{ marginLeft: '10px' }} />
                </Tooltip>
              </>
            }
            /*
             * 300px was sized for the 3-slot table (a time and a toggle). The E-era cell shows the
             * RULE as a sentence — "Every 2 months at 10:00" — plus its next occurrence, and at
             * 300px that wrapped to one character per line (founder screenshot, 2026-09-10).
             */
            width={380}
            render={(reminders, recordHigher: DayReminders, indx) => {
              const columns = [
                {
                  title: '',
                  dataIndex: 'period',
                  render: (period: string) => {
                    return (
                      <Text strong>
                        {translate(`pages.copingCards.${period}`)}
                      </Text>
                    );
                  },
                },
                {
                  title: '',
                  dataIndex: 'time',
                  render: (
                    time: DayReminders['day']['time'],
                    record: DayReminders['day'],
                  ) => {
                    return (
                      <DateFnsTimePicker
                        size="small"
                        defaultValue={set(new Date(), {
                          hours: time.hours,
                          minutes: time.minutes,
                        })}
                        format={
                          i18n.languages[0]?.startsWith('ru')
                            ? 'HH:mm'
                            : 'hh:mm a'
                        }
                        disabled={!isEditing(recordHigher._id)}
                        onChange={(date: Date | Date[]) => {
                          if (Array.isArray(date)) return;
                          const hours = date?.getHours() || 0;
                          const minutes = date?.getMinutes() || 0;

                          const copiedDayReminders = { ...reminders };
                          copiedDayReminders[
                            // @ts-ignore
                            record.key
                          ].time = {
                            hours,
                            minutes,
                          };
                          updateDateReminders(copiedDayReminders);
                        }}
                      />
                    );
                  },
                },
                {
                  title: '',
                  dataIndex: 'enabled',
                  render: (enabled: boolean, record: DayReminders['day']) => {
                    return (
                      <Checkbox
                        checked={enabled}
                        disabled={!isEditing(recordHigher._id)}
                        onChange={(e) => {
                          const checked = e.target.checked;

                          const copiedDayReminders = { ...reminders };
                          copiedDayReminders[
                            // @ts-ignore
                            record.key
                          ].enabled = checked;
                          updateDateReminders(copiedDayReminders);
                        }}
                      />
                    );
                  },
                },
              ];
              const { _id, ...remindersWithoutId } = reminders;

              const dayRemindersData = Object.keys(
                remindersWithoutId || {},
              ).map((period) => {
                return {
                  key: period,
                  period,
                  ...(remindersWithoutId?.[period] || {}),
                };
              });
              return (
                <CardRemindersCell
                  subject={
                    (recordHigher as unknown as { userId?: string })?.userId
                  }
                  targetRef={(recordHigher as unknown as { _id?: string })?._id}
                  legacy={
                    <Form.Item name={['dayReminders']}>
                      <Table
                        showHeader={false}
                        columns={columns}
                        dataSource={dayRemindersData}
                        pagination={false}
                        size="small"
                      />
                    </Form.Item>
                  }
                />
              );
            }}
          />
          <Table.Column
            key="updatedAt"
            dataIndex="updatedAt"
            title={translate('pages.thoughtList.updatedAt')}
            sorter={{ multiple: 1 }}
            render={(text, record, indx) => {
              const date = new Date(text);
              return format(
                date,
                currentLocale?.startsWith('ru')
                  ? 'yyyy-MM-dd HH:mm:ss'
                  : 'yyyy-MM-dd hh:mm:ss aaa',
              );
            }}
          />
          <Table.Column<Thought>
            title={translate('resourcesCommon.actions')}
            dataIndex="actions"
            render={(_, record) => {
              const client = clients.find((c) => c._id === record.userId);

              const hasPremiumAccess = client?.accessLevel;

              const canEdit =
                client?.access?.allowEdit ||
                (client?.access?.allowAdminEdit &&
                  identity?.db?.role === UserRole.THERAPIST_ADMIN);

              if (isEditing(record._id)) {
                return (
                  <Space>
                    <SaveButton
                      data-testid="cc-card-save-button"
                      {...saveButtonProps}
                      onClick={() => {
                        const resource =
                          t(
                            'pages.clientsShow.therapistOtherResources.portalCards',
                          ) +
                          '/' +
                          (record._id.split('/')[1] || '');
                        therapistActivitySend({
                          type: 'UPDATED',
                          resource,
                        });
                        saveButtonProps.onClick?.();

                        // back to the list for ITS client (on the client page the header's filter is empty — review F7)
                        therapistActivitySend(
                          backToListEvent(
                            t('pages.clientsShow.therapistOtherResources.portalCards'),
                            clientFilterId,
                          ),
                        );
                      }}
                      // disabled={isSubmitDisabled}
                      size="small"
                    />
                    <Button
                      {...cancelButtonProps}
                      size="small"
                      onClick={() => {
                        const resource =
                          t(
                            'pages.clientsShow.therapistOtherResources.portalCards',
                          ) +
                          '/' +
                          (record._id.split('/')[1] || '');
                        therapistActivitySend({
                          type: 'TIMED_OUT',
                          resource,
                        });
                        cancelButtonProps.onClick?.();
                        // back to the list for ITS client (on the client page the header's filter is empty — review F7)
                        therapistActivitySend(
                          backToListEvent(
                            t('pages.clientsShow.therapistOtherResources.portalCards'),
                            clientFilterId,
                          ),
                        );
                      }}
                    >
                      {translate('buttons.cancel')}
                    </Button>
                  </Space>
                );
              }
              return (
                <Space>
                  {
                    <>
                      <EditButton
                        data-testid="cc-card-edit-button"
                        {...editButtonProps(record._id)}
                        onClick={() => {
                          const resource =
                            t(
                              'pages.clientsShow.therapistOtherResources.portalCards',
                            ) +
                            '/' +
                            (record._id.split('/')[1] || '');
                          therapistActivitySend({
                            type: 'TIMED_OUT',
                            resource: t(
                              'pages.clientsShow.therapistOtherResources.portalCards',
                            ),
                          });
                          therapistActivitySend({
                            type: 'LOADED',
                            resource,
                            clientId: record.userId,
                          });
                          editButtonProps(record._id).onClick?.();
                        }}
                        hideText
                        size="small"
                        disabled={!hasPremiumAccess || !canEdit}
                      />
                      {(!hasPremiumAccess || !canEdit) && (
                        <Tooltip
                          title={translate(
                            !hasPremiumAccess
                              ? 'pages.thoughtList.thoughtEditLimitTip'
                              : 'pages.thoughtList.thoughtEditNoPermissionTip',
                          )}
                        >
                          <QuestionCircleOutlined
                            style={{ marginLeft: '10px' }}
                          />
                        </Tooltip>
                      )}
                    </>
                  }
                </Space>
              );
            }}
          />
        </Table>
      </Form>
    </ToolList>
  );
};
